import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyListitemActivateDetail = { item: SkyListitem };

/**
 * @element sky-listitem
 *
 * @summary List item with optional avatar, actions, subheader mode, and selectable state.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/list
 *
 * @slot - Fallback label text when `content` is not used.
 * @slot content - Main content of the list item (usually label or text).
 * @slot actions - Trailing action elements (e.g., icons, buttons).
 * @property {string} avatar - Optional avatar image URL.
 * @property {string} value - Stable id for selection (`sky-list` `selectedValues` / `list-change`). Default: `""`.
 * @property {string} subheader - Subheader text; when set, item renders as a section header.
 * @property {boolean} selected - Whether item is selected.
 * @property {boolean} dense - Whether compact spacing is used.
 * @property {boolean} disabled - Whether interaction is disabled.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @fires {CustomEvent<SkyListitemActivateDetail>} item-activate - Fired when the item is activated.
 * @method setSelected Sets selection state programmatically.
 *
 * @csspart list-item - The main list item container.
 * @csspart leading - The leading section (avatar container).
 * @csspart avatar - The avatar image element.
 * @csspart content - The content wrapper for the main slot.
 * @csspart actions - The trailing actions container.
 * @csspart subheader - The subheader divider element.
 *
 * @Behavior
 * - Renders either a selectable list row or a non-interactive subheader variant.
 * - Emits `item-activate` for parent list orchestration.
 * - Supports compact and disabled display variants.
 *
 * @example
 * ```html
 * <sky-listitem avatar="https://example.com/avatar.jpg" selected>
 *   <span slot="content">John Doe</span>
 *   <button slot="actions" type="button">Action</button>
 * </sky-listitem>
 * ```
 * ```vue
 * <template>
 *   <sky-listitem avatar="https://example.com/avatar.jpg" selected @item-activate="onActivate">
 *     <span slot="content">John Doe</span>
 *     <button slot="actions" type="button">Action</button>
 *   </sky-listitem>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-listitem avatar="https://example.com/avatar.jpg" selected>
 *       <span slot="content">John Doe</span>
 *       <button slot="actions" type="button">Action</button>
 *     </sky-listitem>
 *   );
 * }
 * ```
 */
@customElement("sky-listitem")
export class SkyListitem extends LitElement {
  @property({ type: String }) avatar = "";
  /** Optional stable value for list selection (`sky-list` binding). */
  @property({ type: String }) value = "";
  @property({ type: String, reflect: true }) subheader = "";
  @property({ type: Boolean, reflect: true }) selected = false;
  @property({ type: Boolean, reflect: true }) dense = false;
  @property({ type: Boolean, reflect: true }) disabled = false;


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: block;
      cursor: pointer;
      outline: none;
    }

    :host([subheader]) {
      cursor: default;
    }

    :host([disabled]) {
      opacity: var(--sky-opacity-disabled, 0.6);
      pointer-events: none;
      cursor: not-allowed;
    }

    .list-item {
      display: flex;
      align-items: center;
      padding: var(--sky-space-2\\.5, 10px) var(--sky-space-3\\.5, 14px);
      border-bottom: var(--sky-border-primary);
      border-inline-start: 3px solid transparent;
      transition:
        background var(--sky-transition-normal, 200ms ease),
        border-color var(--sky-transition-normal, 200ms ease),
        box-shadow var(--sky-transition-normal, 200ms ease);
      color: var(--sky-text-primary);
      position: relative;
      box-sizing: border-box;
    }

    :host(:hover:not([disabled])) .list-item,
    :host(:focus-visible) .list-item {
      background: var(--sky-hover-primary);
    }

    :host(:focus-visible) .list-item {
      box-shadow: var(--sky-focus-ring);
    }

    :host([selected]) .list-item {
      background: var(--sky-hover-secondary);
      border-inline-start-color: var(--sky-active-primary);
    }

    :host(:last-child) .list-item {
      border-bottom: none;
    }

    .leading {
      display: flex;
      align-items: center;
      margin-inline-end: var(--sky-space-3, 12px);
      flex-shrink: 0;
    }

    .avatar {
      width: 40px;
      height: 40px;
      border-radius: var(--sky-radius-full);
      object-fit: cover;
      background: var(--sky-hover-tertiary);
    }

    :host([dense]) .list-item {
      padding: var(--sky-space-1\\.5, 6px) var(--sky-space-2\\.5, 10px);
    }

    :host([dense]) .avatar {
      width: 32px;
      height: 32px;
    }

    .content {
      flex: 1 1 auto;
      min-width: 0;
      text-align: start;
      font-family: var(--sky-font);
    }

    .actions {
      display: flex;
      align-items: center;
      margin-inline-start: var(--sky-space-3, 12px);
      gap: var(--sky-space-2, 8px);
      flex-shrink: 0;
    }

    .subheader {
      font-family: var(--sky-font);
      font-size: var(--sky-font-size-sm);
      text-align: start;
      color: var(--sky-text-secondary);
      padding: var(--sky-space-1\\.5, 6px) var(--sky-space-3\\.5, 14px);
      font-weight: var(--sky-font-weight-semibold);
      background: var(--sky-hover-tertiary);
      border-bottom: var(--sky-border-primary);
    }

    @media (prefers-reduced-motion: reduce) {
      .list-item {
        transition: none;
      }
    }
  `;

  private onClick(): void {
    if (this.disabled || this.subheader) return;
    this.dispatchEvent(
      new CustomEvent<SkyListitemActivateDetail>("item-activate", {
        detail: { item: this },
        bubbles: true,
        composed: true,
      }),
    );
  }

  setSelected(selected: boolean): void {
    if (this.selected === selected) return;
    this.selected = selected;
  }

  override render() {
    if (this.subheader) {
      return html`
        <div class="subheader" part="subheader" role="presentation">${this.subheader}</div>
      `;
    }

    return html`
      <div
        class="list-item"
        part="list-item"
        role="option"
        aria-selected=${this.selected ? "true" : "false"}
        aria-disabled=${this.disabled ? "true" : "false"}
        tabindex="-1"
        @click=${this.onClick}
      >
        ${when(
          this.avatar,
          () => html`
            <div class="leading" part="leading">
              <img
                class="avatar"
                src=${this.avatar}
                alt=""
                part="avatar"
              />
            </div>
          `,
        )}

        <div class="content" part="content">
          <slot name="content"><slot></slot></slot>
        </div>

        <div class="actions" part="actions">
          <slot name="actions">${nothing}</slot>
        </div>
      </div>
    `;
  }
}